/**
 * StaffSystemHealth — IT-SCE Intern: Live-Status aller Services + Infrastruktur.
 *
 * TQ #9697 (Staff-Migration Teil D, Bundle #9523). Portiert aus
 * customer-portal/src/pages/staff/StaffSystemHealth.tsx — gleiche
 * API-Calls (api.staffGetHealthSummary/staffListHealthServices/…),
 * Styling an StaffKnowledge.tsx (org-portal, TQ #7128) angeglichen.
 *
 * Routing: outer-router-level (App.tsx) unter /staff/health, hinter
 * StaffOnly-Guard, gerendert über StaffHealthHub (Tab "System-Health").
 */

import { useEffect, useState } from 'react'
import { api, StaffHealthSummary, StaffHealthService, StaffHealthIncident } from '../../services/api'
import { useToast } from '../../components/ui'

const STATUS_DOT: Record<string, string> = {
  healthy: '#10b981',
  ok: '#10b981',
  degraded: '#f59e0b',
  unhealthy: '#ef4444',
  down: '#ef4444',
  unknown: '#9ca3af',
}

const STATUS_LABEL: Record<string, string> = {
  healthy: 'OK',
  ok: 'OK',
  degraded: 'Degraded',
  unhealthy: 'Down',
  down: 'Down',
  unknown: '?',
}

function fmtRelative(iso?: string): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return '—'
  const s = Math.round(ms / 1000)
  if (s < 60) return `vor ${s}s`
  if (s < 3600) return `vor ${Math.round(s / 60)}min`
  if (s < 86400) return `vor ${Math.round(s / 3600)}h`
  return `vor ${Math.round(s / 86400)}d`
}

export default function StaffSystemHealth() {
  const [summary, setSummary] = useState<StaffHealthSummary | null>(null)
  const [services, setServices] = useState<StaffHealthService[]>([])
  const [incidents, setIncidents] = useState<StaffHealthIncident[]>([])
  const [offline, setOffline] = useState(false)
  const [offlineReason, setOfflineReason] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const { showToast } = useToast()

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const [summaryResp, servicesResp, incidentsResp] = await Promise.all([
          api.staffGetHealthSummary(),
          api.staffListHealthServices(),
          api.staffListHealthIncidents().catch(() => ({ incidents: [] })),
        ])
        if (cancelled) return
        setSummary(summaryResp)
        setServices(servicesResp.services ?? [])
        setIncidents(incidentsResp.incidents ?? [])
        const isOffline = !!(summaryResp.offline || servicesResp.offline)
        setOffline(isOffline)
        setOfflineReason(isOffline ? (servicesResp.message ?? summaryResp.message ?? null) : null)
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String((err as { error?: string })?.error ?? err)
        showToast('error', `Fehler beim Laden: ${msg}`)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    const t = setInterval(load, 30_000)
    return () => { cancelled = true; clearInterval(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const counts = summary?.counts
  const overallColor = STATUS_DOT[summary?.overall ?? 'unknown'] ?? STATUS_DOT.unknown

  return (
    <div>
      <p style={{ color: '#666', marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem' }}>
        Live-Status aller Services und Infrastruktur (Auto-Refresh 30s)
      </p>

      {offline && (
        <div style={{ padding: 12, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, marginBottom: 16 }}>
          <strong>⚠ Health-Monitor offline.</strong>
          {offlineReason && <span style={{ marginLeft: 6 }}>{offlineReason}</span>}
          <div style={{ color: '#92400e', fontSize: 13, marginTop: 4 }}>
            admin-api kann den Health-Monitor-Service nicht erreichen — Status-Werte stehen auf "unknown".
            Backend-Job pruefen (Deployment health-monitor, Namespace platform).
          </div>
        </div>
      )}

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
          <Card label="Gesamt-Status" value={STATUS_LABEL[summary.overall] ?? summary.overall} statusColor={overallColor} />
          {counts && (
            <>
              <Card label="Services OK" value={`${counts.healthy} / ${counts.total}`} statusColor="#10b981" />
              <Card label="Degraded" value={String(counts.degraded)} statusColor={counts.degraded > 0 ? '#f59e0b' : '#9ca3af'} />
              <Card label="Down" value={String(counts.unhealthy)} statusColor={counts.unhealthy > 0 ? '#ef4444' : '#9ca3af'} />
              <Card label="Unbekannt" value={String(counts.unknown)} statusColor={counts.unknown > 0 ? '#9ca3af' : '#9ca3af'} />
            </>
          )}
        </div>
      )}

      <h2 style={{ fontSize: 18, marginTop: 24, marginBottom: 12 }}>Services</h2>
      {loading && services.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Lade Services…</div>
      ) : services.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Keine Services überwacht.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
          {services.map(s => (
            <div key={s.name} style={{ padding: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_DOT[s.status] ?? STATUS_DOT.unknown, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.displayName ?? s.name}
                </div>
                <div style={{ color: '#9ca3af', fontSize: 12, display: 'flex', gap: 8 }}>
                  <span>{STATUS_LABEL[s.status] ?? s.status}</span>
                  <span>· {fmtRelative(s.lastCheck)}</span>
                  {s.responseTimeMs != null && s.responseTimeMs > 0 && <span>· {s.responseTimeMs}ms</span>}
                  {s.uptime24h != null && <span>· {s.uptime24h.toFixed(1)}% (24h)</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {incidents.length > 0 && (
        <>
          <h2 style={{ fontSize: 18, marginTop: 24, marginBottom: 12 }}>Aktive Incidents</h2>
          <div style={{ display: 'grid', gap: 8 }}>
            {incidents.filter(i => i.status !== 'resolved').map(i => (
              <div key={i.id} style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
                <div style={{ fontWeight: 600 }}>
                  {i.service} <span style={{ color: '#991b1b', fontSize: 12 }}>({i.severity})</span>
                </div>
                <div style={{ color: '#6b7280', fontSize: 13 }}>{i.description ?? '(keine Beschreibung)'}</div>
                <div style={{ color: '#9ca3af', fontSize: 11 }}>Seit {fmtRelative(i.started_at)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function Card({ label, value, statusColor }: { label: string; value: string; statusColor: string }) {
  return (
    <div style={{ padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor }} />
        <span style={{ fontSize: 18, fontWeight: 600 }}>{value}</span>
      </div>
    </div>
  )
}
